import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  requisitionApi,
  type CreateRequisitionRequest,
  type RequisitionListParams,
  type Requisition,
  type UpdateRequisitionRequest,
  type ApproveRequisitionRequest,
  type RejectRequisitionRequest,
  type ReturnRequisitionItemRequest,
  type ApproveRequisitionItemReturnRequest,
  type RejectRequisitionItemReturnRequest,
  RequisitionStatus,
} from "../services/requisitionApi";

type RequisitionApiResponse = Partial<Requisition> & {
  requisitionStatus?: number | string | null;
  status?: number | string | null;
  requestedByUserName?: string | null;
};

// Normalize status to numeric value and map backend field to frontend
const normalizeRequisition = (req: RequisitionApiResponse): Requisition => ({
  id: req.id ?? "",
  requisitionNumber: req.requisitionNumber,
  requiredByDate: req.requiredByDate ?? "",
  purpose: req.purpose ?? "",
  departmentId: req.departmentId ?? "",
  departmentName: req.departmentName ?? "",
  greenhouseId: req.greenhouseId,
  greenhouseName: req.greenhouseName,
  farmId: req.farmId,
  farmName: req.farmName,
  fieldId: req.fieldId,
  fieldName: req.fieldName,
  notes: req.notes ?? "",
  items: req.items ?? [],
  status: normalizeStatus(req.requisitionStatus ?? req.status),
  requisitionStatus: normalizeStatus(req.requisitionStatus ?? req.status),
  requestedByUserName: req.requestedByUserName,
  requiresProcurement: req.requiresProcurement,
  createdAt: req.createdAt ?? "",
  createdBy: req.requestedByUserName || req.createdBy || "Unknown",
});

const normalizeStatus = (status: number | string | null | undefined): number => {
  if (typeof status === "number") return status;
  if (typeof status === "string") {
    const normalized = status.replace(/\s+/g, "").toLowerCase();
    const byName: Record<string, number> = {
      draft: RequisitionStatus.Draft,
      pending: RequisitionStatus.PendingApproval,
      pendingapproval: RequisitionStatus.PendingApproval,
      approved: RequisitionStatus.Approved,
      partiallyissued: RequisitionStatus.PartiallyIssued,
      issued: RequisitionStatus.Issued,
      rejected: RequisitionStatus.Rejected,
      cancelled: RequisitionStatus.Cancelled,
    };
    const parsed = parseInt(status);
    return byName[normalized] ?? (isNaN(parsed) ? RequisitionStatus.Draft : parsed);
  }
  return RequisitionStatus.Draft;
};

type RequisitionView = NonNullable<RequisitionListParams["view"]>;

const invalidateRequisitionViews = (views: RequisitionView[]) => {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const [feature, scope, params] = query.queryKey;
      if (feature !== "requisitions") return false;
      if (scope !== "paged") return true;
      const view = typeof params === "object" && params !== null && "view" in params
        ? (params as { view?: RequisitionView }).view
        : undefined;
      return !view || views.includes(view);
    },
  });
};

export const useRequisitions = (pageNumber: number, pageSize: number, search?: string) => {
  return useQuery({
    queryKey: ["requisitions", pageNumber, pageSize, search],
    queryFn: async () => {
      const result = await requisitionApi.getAllRequisitions(pageNumber, pageSize, search);
      return {
        ...result,
        items: result.items.map(normalizeRequisition),
      };
    },
  });
};

export const usePagedRequisitions = (params: RequisitionListParams, enabled = true) => {
  return useQuery({
    queryKey: ["requisitions", "paged", params],
    queryFn: async () => {
      const result = await requisitionApi.getRequisitionsPaged(params);
      return {
        ...result,
        items: result.items.map(normalizeRequisition),
      };
    },
    enabled,
    staleTime: Infinity,
  });
};

export const useMyRequisitions = () => {
  return useQuery({
    queryKey: ["requisitions", "my"],
    queryFn: async () => {
      const requisitions = await requisitionApi.getMyRequisitions();
      return requisitions.map(normalizeRequisition);
    },
  });
};

export const usePendingApprovals = () => {
  return useQuery({
    queryKey: ["requisitions", "pending-approvals"],
    queryFn: async () => {
      const requisitions = await requisitionApi.getPendingApprovals();
      return requisitions.map(normalizeRequisition);
    },
  });
};

export const usePendingIssue = () => {
  return useQuery({
    queryKey: ["requisitions", "pending-issue"],
    queryFn: async () => {
      const requisitions = await requisitionApi.getPendingIssue();
      return requisitions.map(normalizeRequisition);
    },
  });
};

export const useRequisitionsByDateRange = (
  startDate: string,
  endDate: string
) => {
  return useQuery({
    queryKey: ["requisitions", "dateRange", startDate, endDate],
    queryFn: async () => {
      const requisitions = await requisitionApi.getRequisitionsByDateRange(startDate, endDate);
      return requisitions.map(normalizeRequisition);
    },
    enabled: !!startDate && !!endDate,
  });
};

export const useRequisitionsByGreenhouse = (greenhouseId: string) => {
  return useQuery({
    queryKey: ["requisitions", "greenhouse", greenhouseId],
    queryFn: async () => {
      const requisitions = await requisitionApi.getRequisitionsByGreenhouse(greenhouseId);
      return requisitions.map(normalizeRequisition);
    },
    enabled: !!greenhouseId,
  });
};

export const useRequisitionById = (id: string) => {
  return useQuery({
    queryKey: ["requisitions", id],
    queryFn: async () => {
      const requisition = await requisitionApi.getRequisitionById(id);
      return normalizeRequisition(requisition);
    },
    enabled: !!id,
  });
};

export const useCreateRequisition = () => {
  return useMutation({
    mutationFn: (payload: CreateRequisitionRequest) =>
      requisitionApi.createRequisition(payload),
    onSuccess: (_data, variables) => {
      invalidateRequisitionViews(["all", "mine", "pendingApproval"]);
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
      const siIds = variables.items.map((i) => i.sowingInstructionId).filter(Boolean);
      for (const siId of siIds) {
        queryClient.invalidateQueries({ queryKey: ["sowing-requisition", siId] });
        queryClient.invalidateQueries({ queryKey: ["sowing-requisitions", siId] });
      }
    },
  });
};

export const useUpdateRequisition = () => {
  return useMutation({
    mutationFn: (payload: UpdateRequisitionRequest) =>
      requisitionApi.updateRequisition(payload),
    onSuccess: (data) => {
      invalidateRequisitionViews(["all", "mine", "pendingApproval", "pendingIssue"]);
      queryClient.invalidateQueries({ queryKey: ["requisitions", data.id] });
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "sowing-requisition" || q.queryKey[0] === "sowing-requisitions" });
    },
  });
};

export const useDeleteRequisition = () => {
  return useMutation({
    mutationFn: (id: string) => requisitionApi.deleteRequisition(id),
    onSuccess: () => {
      invalidateRequisitionViews(["all", "mine"]);
    },
  });
};

export const useApproveRequisition = () => {
  return useMutation({
    mutationFn: (payload: ApproveRequisitionRequest) =>
      requisitionApi.approveRequisition(payload),
    onSuccess: () => {
      invalidateRequisitionViews(["all", "mine", "pendingApproval", "pendingIssue"]);
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "sowing-requisition" || q.queryKey[0] === "sowing-requisitions" });
    },
  });
};

export const useRejectRequisition = () => {
  return useMutation({
    mutationFn: (payload: RejectRequisitionRequest) =>
      requisitionApi.rejectRequisition(payload),
    onSuccess: () => {
      invalidateRequisitionViews(["all", "mine", "pendingApproval"]);
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "sowing-requisition" || q.queryKey[0] === "sowing-requisitions" });
    },
  });
};

export const useReturnRequisitionItem = () => {
  return useMutation({
    mutationFn: (payload: ReturnRequisitionItemRequest) =>
      requisitionApi.returnItem(payload),
    onSuccess: (_data, variables) => {
      invalidateRequisitionViews(["all", "mine", "pendingIssue"]);
      queryClient.invalidateQueries({
        queryKey: ["sowing-requisition", variables.requisitionId],
      });
      queryClient.invalidateQueries({ queryKey: ["pending-returns"] });
      queryClient.invalidateQueries({ queryKey: ["my-returns"] });
    },
  });
};

export const usePendingRequisitionReturns = () => {
  return useQuery({
    queryKey: ["pending-returns"],
    queryFn: () => requisitionApi.getPendingReturns(),
  });
};

export const useMyRequisitionReturns = () => {
  return useQuery({
    queryKey: ["my-returns"],
    queryFn: () => requisitionApi.getMyReturns(),
  });
};

export const useApproveRequisitionReturn = () => {
  return useMutation({
    mutationFn: (payload: ApproveRequisitionItemReturnRequest) =>
      requisitionApi.approveReturn(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-returns"] });
      invalidateRequisitionViews(["all", "mine", "pendingIssue"]);
    },
  });
};

export const useRejectRequisitionReturn = () => {
  return useMutation({
    mutationFn: (payload: RejectRequisitionItemReturnRequest) =>
      requisitionApi.rejectReturn(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-returns"] });
    },
  });
};

export const useRequisitionForSowing = (sowingInstructionId: string | undefined) => {
  return useQuery({
    queryKey: ["sowing-requisition", sowingInstructionId],
    queryFn: () => requisitionApi.getRequisitionForSowing(sowingInstructionId!),
    enabled: !!sowingInstructionId,
  });
};

// Plural — returns ALL requisitions linked to a sowing instruction. For partial
// (tray-mode) sowings the user may accumulate one requisition per batch, so the
// Record Sowing dialog uses this to pick the right one for the next batch.
export const useRequisitionsForSowing = (sowingInstructionId: string | undefined) => {
  return useQuery({
    queryKey: ["sowing-requisitions", sowingInstructionId],
    queryFn: async () => {
      const raw = await requisitionApi.getRequisitionsForSowing(sowingInstructionId!);
      return raw.map((r: any) => normalizeRequisition(r));
    },
    enabled: !!sowingInstructionId,
  });
};

type FilterType = "all" | "my" | "pending-approvals" | "pending-issue";

const VIEW_BY_FILTER: Record<FilterType, RequisitionListParams["view"]> = {
  all: "all",
  my: "mine",
  "pending-approvals": "pendingApproval",
  "pending-issue": "pendingIssue",
};

export const useFilteredRequisitions = (
  filterType: FilterType,
  params: Omit<RequisitionListParams, "view">,
  enabled = true,
) => {
  return usePagedRequisitions(
    {
      ...params,
      view: VIEW_BY_FILTER[filterType],
    },
    enabled,
  );
};
